import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { t } from '@lingui/core/macro';

import CloseIcon from '@/assets/svg/bx-x.svg?react';
import { Button } from '@/components/buttons';
import { cn } from '@/lib/utils';

import { useComboboxContext } from './ComboboxContext';

export type ComboboxChipProps = ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
  disabled?: boolean;
};

const pickerControlChipSizeClasses = {
  small: cn('h-5'),
  medium: cn('h-6'),
  large: cn('h-7'),
};

const closeButtonSizeClasses = {
  small: cn('size-5'),
  medium: cn('size-5.5'),
  large: cn('size-6'),
};

const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  disabled: disabledProp = false,
  ...props
}: ComboboxChipProps) => {
  const { size, disabled: disabledRoot = false } = useComboboxContext();
  const disabled = disabledProp || disabledRoot;

  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      aria-disabled={disabled}
      className={cn(
        'inline-flex max-w-full min-w-0 items-center gap-1 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap',
        'bg-green-300 text-gray-900',
        'focus-visible:bg-green-400 focus-visible:outline-green-500',
        'has-data-[slot=combobox-chip-remove]:pr-0',
        'aria-disabled:cursor-not-allowed aria-disabled:bg-gray-200! aria-disabled:text-gray-500!',
        pickerControlChipSizeClasses[size],
        className,
      )}
      {...props}
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          data-slot="combobox-chip-remove"
          className="mr-0.5 -ml-0.5 opacity-50 hover:opacity-100 disabled:pointer-events-none disabled:text-gray-500!"
          render={
            <Button
              className={cn('-mr-0.5 min-w-0 p-0', closeButtonSizeClasses[size])}
              aria-label={t`Remove item`}
              disabled={disabled}
              variant="ghost"
              size="icon-small"
              shape="round"
            >
              <CloseIcon className="pointer-events-none" />
            </Button>
          }
        />
      )}
    </ComboboxPrimitive.Chip>
  );
};

export default ComboboxChip;
